import { makeStyles } from '@material-ui/styles'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import InfoPanel from 'core/components/InfoPanel'
import SimpleLink from 'core/components/SimpleLink'
import TextField from 'core/components/validatedForm/TextField'
import Badge from 'core/elements/badge/Badge'
import Banner from 'core/elements/banner/Banner'
import Button from 'core/elements/button'
import Grid, { type GridViewColumn } from 'core/elements/grid/Grid'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import type Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import { listImages } from 'openstack/components/images/actions'
import { imagesSelector, resolveSourceVmId } from 'openstack/components/images/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { durationBetweenDates } from 'utils/misc'
import { listVolumeSnapshots } from '../storage/snapshots/actions'
import { volumeSnapshotsByIdSelector } from '../storage/snapshots/selectors'
import { IVolumeDetailsPageTabs } from '../storage/volumes/volume-details/model'
import { isVolumeBackedVm } from './helpers'
import RevertRunTracker from './revert-runs/RevertRunTracker'
import { useRevertVmRunnerContext } from './revert-runs/RevertVmRunnerContext'
import { useHideTrayWhileOpen } from './revert-runs/RevertVmTray'
import { isRunActive } from './revert-runs/revertRuns'

interface Props {
  // VM row actions pass the selected rows; reverting works on a single VM
  // (see ACTION_LIMITATION_TOOLTIPS.REVERT_VM_TO_SNAPSHOT)
  rows: Array<{
    id: string
    name?: string
    volumeIds?: string[]
    volumeDetails?: any
  }>
  onClose: (success?: boolean) => void
}

/**
 * Drawer for reverting a VM to its most recent snapshot. The revert itself runs in the app-wide
 * runner (see revert-runs/), so it keeps going after this drawer closes.
 *
 * See RevertVmToSnapshotModal.spec.md for RVTS-FR-001, RVTS-FR-002, RVTS-FR-003, RVTS-FR-004,
 * RVTS-FR-005, RVTS-FR-006, RVTS-FR-007 and RVTS-FR-008
 */
export default function RevertVmToSnapshotModal({ rows: [vm], onClose }: Props) {
  const classes = useStyles()
  const confirmTarget = vm?.name || vm?.id
  const isVolumeBacked = isVolumeBackedVm(vm)

  const defaultParams: { confirmName: string } = {
    confirmName: '',
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)

  const { startRun, runs } = useRevertVmRunnerContext()
  useHideTrayWhileOpen()
  const [startedRunId, setStartedRunId] = useState<string | null>(null)
  const startedRun = runs.find((run) => run.id === startedRunId)
  // A VM can only have one revert running at a time
  const activeRun = runs.find((run) => run.vmId === vm?.id && isRunActive(run))
  const hasActiveRun = !!activeRun

  const { loading: loadingImages } = useListAction(listImages)
  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)

  const images = useSelector(imagesSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector as any) as Record<string, any>

  const mostRecentSnapshot = useMemo(() => {
    if (!vm || !images?.length) {
      return null
    }
    const wantedType = isVolumeBacked ? 'vmVolumeSnapshot' : 'ephemeralDiskSnapshot'
    const vmVolumeIds = new Set(vm.volumeIds ?? [])
    const snapshotsForVm = images
      .filter((image) => {
        if (image.imageType !== wantedType) return false
        if (resolveSourceVmId(image) === vm.id) return true
        if (!isVolumeBacked) return false
        return (image.snapshotIds ?? []).some((snapshotId: string) =>
          vmVolumeIds.has(volumeSnapshotsById?.[snapshotId]?.volume_id),
        )
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    return snapshotsForVm?.[0]
  }, [vm, images, isVolumeBacked, volumeSnapshotsById])

  const vmVolumesByType = useMemo(() => {
    const volumesByType: {
      matching: DiskRevertSource[]
      nonMatching: DiskRevertSource[]
      missingIds: string[]
    } = { matching: [], nonMatching: [], missingIds: [] }
    if (!isVolumeBacked || !vm?.volumeIds?.length || !mostRecentSnapshot) {
      return volumesByType
    }

    const targetSnapshotIds: string[] = mostRecentSnapshot.snapshotIds ?? []
    const targetSnapshotIdSet = new Set(targetSnapshotIds)

    // Group volume snapshots by volume, newest first
    const snapshotsByVolumeId: Record<string, any[]> = {}
    for (const snapshot of Object.values(volumeSnapshotsById ?? {})) {
      snapshotsByVolumeId[snapshot.volume_id] ??= []
      snapshotsByVolumeId[snapshot.volume_id].push(snapshot)
    }
    for (const snapshots of Object.values(snapshotsByVolumeId)) {
      snapshots.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    }

    const matchedSnapshotIds = new Set<string>()
    for (const volumeId of vm.volumeIds) {
      // VM rows carry the readable name as `volume_name`, not `name`
      const details = vm?.volumeDetails?.[volumeId]
      const volume = {
        ...details,
        id: volumeId,
        name: details?.volume_name || details?.name || volumeId,
      }
      const snapshots = snapshotsByVolumeId[volumeId] ?? []
      const [latest, ...older] = snapshots
      const matchedSnapshot = snapshots.find((snapshot) => targetSnapshotIdSet.has(snapshot.id))
      if (matchedSnapshot) {
        matchedSnapshotIds.add(matchedSnapshot.id)
      }

      if (latest && targetSnapshotIdSet.has(latest.id)) {
        volumesByType.matching.push({ volume, snapshot: latest })
      } else if (matchedSnapshot && older.includes(matchedSnapshot)) {
        volumesByType.nonMatching.push({
          volume,
          snapshot: matchedSnapshot,
          reason: `${volume.name} has a newer snapshot, ${latest.name || latest.id} (${moment(latest.created_at).format(DATE_FORMAT)}).`,
          detail:
            'A volume can only be reverted to its latest snapshot. Delete the newer volume snapshot to make this VM snapshot the latest again, or use Deploy VM from Snapshot.',
        })
      } else {
        volumesByType.nonMatching.push({
          volume,
          reason: snapshots.length
            ? `None of the snapshots of ${volume.name} belong to the target snapshot.`
            : `${volume.name} has no snapshots.`,
          detail:
            'This VM snapshot can’t restore this volume. Use Deploy VM from Snapshot with another snapshot, or take a new VM snapshot.',
        })
      }
    }

    volumesByType.missingIds = targetSnapshotIds.filter((id) => !matchedSnapshotIds.has(id))
    return volumesByType
  }, [vm, isVolumeBacked, mostRecentSnapshot, volumeSnapshotsById])

  const confirmNameValidations = useMemo(
    () => [customValidator((value) => value === confirmTarget, 'Entered name is incorrect.')],
    [confirmTarget],
  )
  const diskRevertColumns = useMemo(() => getDiskRevertColumns(classes), [classes])
  const diskRevertRows = useMemo(() => buildDiskRevertRows(vmVolumesByType), [vmVolumesByType])
  const diskIssues = useMemo(
    () => [
      ...vmVolumesByType.nonMatching.map(({ volume, reason, detail }) => ({
        key: volume.id,
        reason,
        detail,
      })),
      ...vmVolumesByType.missingIds.map((id) => ({
        key: id,
        reason: `The volume snapshot ${volumeSnapshotsById?.[id]?.name || id} no longer exists.`,
        detail:
          'This VM snapshot can’t restore the volume it covered. Use Deploy VM from Snapshot with another snapshot, or take a new VM snapshot.',
      })),
    ],
    [vmVolumesByType, volumeSnapshotsById],
  )

  const dataVolumes = useMemo(
    () => vmVolumesByType.matching.filter(({ volume }) => volume.boot_index !== 0),
    [vmVolumesByType],
  )

  const handleSubmit = useCallback(async () => {
    if (!mostRecentSnapshot) return
    setStartedRunId(
      startRun({
        vm: { id: vm.id, name: vm.name },
        snapshot: { id: mostRecentSnapshot.id, name: mostRecentSnapshot.name },
        volumes: dataVolumes.map(({ volume, snapshot }) => ({
          id: volume.id,
          name: volume.name,
          snapshotId: snapshot.id,
        })),
      }),
    )
  }, [startRun, vm, mostRecentSnapshot, dataVolumes])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    onClose(success)
  }

  return (
    <ModalForm
      open
      panel="drawer"
      title="Revert VM to Snapshot"
      onSubmit={handleSubmit}
      onClose={() => handleClose(!!startedRun)}
      hideSubmit={!!startedRun}
      closeText={startedRun ? 'Close' : 'Cancel'}
      submitTitle="Revert"
      loading={loadingImages || loadingVolumeSnapshots}
      disableSubmit={
        params.confirmName !== confirmTarget ||
        !mostRecentSnapshot ||
        !!diskIssues.length ||
        hasActiveRun
      }
      maxWidth={800}
    >
      {startedRun ? (
        <RevertRunTracker run={startedRun} />
      ) : (
        <div className={classes.container}>
          {activeRun && (
            <Banner
              icon="triangle-exclamation"
              variant="warning"
              title="A revert is already in progress for this VM"
            >
              <Text variant="body2">
                You can start another revert once the current one finishes.
              </Text>
              <Button variant="secondary" onClick={() => setStartedRunId(activeRun.id)}>
                Show progress
              </Button>
            </Banner>
          )}
          <Banner
            icon="circle-exclamation"
            variant="error"
            title="This operation is destructive and cannot be undone"
          >
            <Text variant="body2">
              The virtual machine is rebuilt from the snapshot, which restores its boot disk. Then
              each attached data volume is detached, reverted to its snapshot and reattached.
              Everything written to disk after the snapshot was taken is discarded.
            </Text>
          </Banner>
          <Banner icon="circle-info" variant="informative" title="Need an earlier snapshot?">
            <Text variant="body2">
              Revert restores this virtual machine to its most recent snapshot. Earlier snapshots
              cannot be used as a revert target — to bring up a virtual machine from an older point
              in time, use Deploy VM from Snapshot, which leaves this virtual machine and its
              snapshot history untouched.
            </Text>
          </Banner>
          <Text variant="body2">
            Revert <b>{confirmTarget}</b> to its most recent snapshot.
          </Text>
          <InfoPanel
            title="Target snapshot"
            dataTestId="target-snapshot"
            items={{
              Name: { value: mostRecentSnapshot?.name ?? '-' },
              Timestamp: {
                value: mostRecentSnapshot?.created_at
                  ? moment(mostRecentSnapshot.created_at).format(DATE_FORMAT)
                  : '-',
              },
              Age: {
                value: mostRecentSnapshot?.created_at
                  ? durationBetweenDates({ labels: ['d', 'h', 'm'] })(
                      mostRecentSnapshot.created_at,
                    ) || 'Less than a minute'
                  : '-',
              },
              Owner: {
                value: mostRecentSnapshot?.owner_user_name || mostRecentSnapshot?.owner || '-',
              },
              'File size': { value: humanReadableSize(mostRecentSnapshot?.size) },
              Tags: {
                value: mostRecentSnapshot?.tags?.length ? (
                  <div className={classes.tagsRow}>
                    {mostRecentSnapshot.tags.map((tag: string) => (
                      <Badge key={tag} variant="default" text={tag} />
                    ))}
                  </div>
                ) : (
                  '-'
                ),
              },
            }}
          />
          <InfoPanel title="Impact" dataTestId="snapshot-impact">
            <div className={classes.impactBody}>
              <div className={classes.impactGroup}>
                {IMPACT_WARNINGS.map((text) => (
                  <div className={classes.impactTile} key={text}>
                    <FontAwesomeIcon className={classes.impactIconError}>
                      circle-exclamation
                    </FontAwesomeIcon>
                    <Text variant="caption1" component="span">
                      {text}
                    </Text>
                  </div>
                ))}
              </div>
              <div className={classes.impactGroup}>
                <Text variant="caption2" component="span">
                  Doesn’t change
                </Text>
                <div className={classes.impactGroup}>
                  {IMPACT_UNCHANGED.map((text) => (
                    <div className={classes.impactTile} key={text}>
                      <FontAwesomeIcon className={classes.impactIconSuccess}>check</FontAwesomeIcon>
                      <Text variant="caption1" component="span">
                        {text}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </InfoPanel>
          <InfoPanel
            title={`Disks Reverted (${diskRevertRows.length})`}
            subtitle={
              <span className={classes.disksSubtitle}>
                Volume UUIDs are preserved. The boot disk is restored first by rebuilding the VM,
                then the data volumes are reverted together.
              </span>
            }
            dataTestId="disks-reverted"
          >
            <div className={classes.disksBody}>
              <Grid<DiskRevertRow>
                uniqueIdentifier="id"
                columns={diskRevertColumns}
                data={diskRevertRows}
                disableToolbar
                disablePagination
                disableRowSelection
              />
              {diskIssues.map(({ key, reason, detail }) => (
                <div className={classes.diskIssue} key={key}>
                  <div className={classes.diskIssueTitle}>
                    <FontAwesomeIcon className={classes.impactIconError}>
                      circle-exclamation
                    </FontAwesomeIcon>
                    <Text variant="caption1" component="span">
                      <b>{reason}</b>
                    </Text>
                  </div>
                  <Text variant="caption1" className={classes.diskIssueDetail}>
                    {detail}
                  </Text>
                </div>
              ))}
            </div>
          </InfoPanel>
          <InfoPanel
            title="Type the instance name to confirm"
            subtitle={
              <>
                Enter <b>{confirmTarget}</b> to enable the revert.
              </>
            }
            dataTestId="confirm-instance-name"
          >
            <div className={classes.confirmBody}>
              <TextField
                id="revertConfirmName"
                label="Instance name"
                required
                value={params.confirmName}
                onChange={getParamsUpdater('confirmName')}
                validations={confirmNameValidations}
              />
            </div>
          </InfoPanel>
        </div>
      )}
    </ModalForm>
  )
}

const IMPACT_WARNINGS = [
  'Data written after the snapshot is lost. Volumes return to their state at that time.',
  'All volumes go offline until the revert finishes.',
]

const IMPACT_UNCHANGED = [
  'The VM keeps its UUID',
  'No snapshots are deleted',
  'IP addresses, network ports, MAC addresses, flavor, security groups, migration priority, ' +
    'metadata and tags.',
]

const DATE_FORMAT = 'MM/DD/YYYY, hh:mm:ss A'

interface DiskRevertSource {
  volume: any
  snapshot?: any
  reason?: string
  detail?: string
}

interface DiskRevertRow {
  id: string
  role: string
  device: string
  check: 'matches' | 'missing' | 'newer'
  volumeId: string
  volumeName: string
  size: string
  snapshotName: string
  snapshotCreatedAt?: string
}

const buildDiskRevertRows = ({
  matching,
  nonMatching,
}: {
  matching: DiskRevertSource[]
  nonMatching: DiskRevertSource[]
}): DiskRevertRow[] =>
  [
    ...matching.map((source) => ({ source, check: 'matches' as const })),
    ...nonMatching.map((source) => ({
      source,
      check: source.snapshot ? ('newer' as const) : ('missing' as const),
    })),
  ]
    // Boot disk first: volumes are reverted in that order
    .sort(
      (a, b) =>
        Number(b.source.volume?.boot_index === 0) - Number(a.source.volume?.boot_index === 0),
    )
    .map(({ source: { volume, snapshot }, check }) => ({
      id: volume.id,
      role: volume.boot_index === 0 ? 'Boot volume' : 'Data volume',
      device: volume.device_name || '-',
      check,
      volumeId: volume.id,
      volumeName: volume.name,
      size: volume.volume_size ? humanReadableSize(volume.volume_size * 1024 * 1024 * 1024) : '-',
      snapshotName: snapshot ? snapshot.name || snapshot.id : '-',
      snapshotCreatedAt: snapshot?.created_at,
    }))

const CHECK_BADGES = {
  matches: { variant: 'success', text: 'Matches' },
  newer: { variant: 'error', text: 'Newer snapshot' },
  missing: { variant: 'error', text: 'Snapshot missing' },
} as const

const getDiskRevertColumns = (
  classes: ReturnType<typeof useStyles>,
): GridViewColumn<DiskRevertRow>[] => [
  {
    key: 'role',
    label: 'Role / Device',
    render: (role: string, row) => (
      <div>
        <Text variant="body2">{role}</Text>
        <Text variant="caption1" className={classes.muted}>
          {row.device}
        </Text>
      </div>
    ),
  },
  {
    key: 'check',
    label: 'Check',
    render: (check: DiskRevertRow['check']) => <Badge {...CHECK_BADGES[check]} ellipsisAt={20} />,
  },
  {
    key: 'volumeName',
    label: 'Attached Volume',
    render: (volumeName: string, row) => (
      <div className={classes.volumeCell}>
        <SimpleLink
          src={routeManager.routes.openstack.volumeDetails.path({
            id: row.volumeId,
            tab: IVolumeDetailsPageTabs.Overview,
          })}
        >
          {volumeName}
        </SimpleLink>
        <span className={classes.muted}>•</span>
        <Text variant="body2" component="span">
          {row.size}
        </Text>
      </div>
    ),
  },
  {
    key: 'snapshotName',
    label: 'Volume Snapshot',
    render: (snapshotName: string, row) => (
      <div>
        <Text variant="body2">{snapshotName}</Text>
        {row.snapshotCreatedAt && (
          <Text variant="caption1" className={classes.muted}>
            {moment(row.snapshotCreatedAt)
              .format(DATE_FORMAT)
              .replace(/:\d{2} /, ' ')}
          </Text>
        )}
      </div>
    ),
  },
]

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  tagsRow: {
    display: 'flex',
    gap: 4,
  },
  impactBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  impactGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  impactTile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.grey['50'],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  impactIconError: {
    color: theme.palette.red[900],
    fontSize: 20,
  },
  impactIconSuccess: {
    color: theme.palette.green[500],
    fontSize: 20,
  },
  disksBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
  },
  disksSubtitle: {
    fontSize: 12,
  },
  muted: {
    color: theme.palette.grey[500],
  },
  volumeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  diskIssue: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.red[50],
    border: `1px solid ${theme.palette.red[200]}`,
    borderRadius: 4,
  },
  diskIssueTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  diskIssueDetail: {
    marginLeft: 28,
  },
  confirmBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3, 3),
    maxWidth: 320,
  },
}))
